import { Component } from 'react';
import shallowCompare from 'react-addons-shallow-compare';
import classNames from 'classnames';
import styled from 'styled-components';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import SyntheticEvent from 'UIComponents/core/SyntheticEvent';
import UIClickable from 'UIComponents/button/UIClickable';
import MultiSelectValue from 'ContentComponents/cmv2/components/MultiSelectValue';
import { getAriaAndDataProps } from 'ContentComponents/cmv2/utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MAX_DROPZONE_WIDTH = 265; // 320px is the width of the select, minus the arrow (33px), minus the input (20px) rounded

const StyledDropZone = styled.div.withConfig({
  displayName: "ReorderableSelect__StyledDropZone",
  componentId: "sc-1wlba1u-0"
})(["display:", ";overflow:auto;vertical-align:middle;width:auto;max-width:", "px;"], props => props.isLastZone ? 'inline-flex' : 'flex', MAX_DROPZONE_WIDTH);
const StyledInput = styled.input.withConfig({
  displayName: "ReorderableSelect__StyledInput",
  componentId: "sc-1wlba1u-1"
})(["width:5px !important;height:100%;box-sizing:content-box !important;"]);
const StyledUIClickable = styled(UIClickable).withConfig({
  displayName: "ReorderableSelect__StyledUIClickable",
  componentId: "sc-1wlba1u-2"
})(["align-self:stretch;flex-grow:1;min-height:40px;"]);
const StyledArrow = styled.div.withConfig({
  displayName: "ReorderableSelect__StyledArrow",
  componentId: "sc-1wlba1u-3"
})(["display:flex !important;align-self:stretch;align-items:center;.Select-arrow{transform:translateY(2px);}"]);
export default class ReorderableSelect extends Component {
  constructor(props) {
    super(props);
    this.onDragEnd = this.onDragEnd.bind(this);
    this.triggerOpen = this.triggerOpen.bind(this);
    this.formatDropZoneListValues = this.formatDropZoneListValues.bind(this);
    this.valueSizesMap = new Map();
    this.state = {
      values: [props.value]
    };
  }
  componentDidMount() {
    this.formatDropZoneListValues();
  }
  shouldComponentUpdate(nextProps, nextState) {
    return shallowCompare(this, nextProps, nextState);
  }
  componentDidUpdate(prevProps, prevState) {
    const {
      onFieldChange
    } = this.props;
    const {
      values
    } = this.state;
    if (JSON.stringify(prevState.values) !== JSON.stringify(values)) {
      onFieldChange(values.flat());
    }
  }
  formatDropZoneListValues(valuesArray = []) {
    // The render function expects an array of arrays, one per list
    // React-beautiful-dnd does not support grid, so everytime values exceed row length,
    // a new list must be created. This function determines how many values can fit per row,
    // and formats the array of arrays accordingly.
    const valuesFormattedArray = [];
    let draggablesCombinedWidthPerDropZone = 0;
    let draggablesArray = [];
    const valueSizesIterable = valuesArray.length ? valuesArray // if values have been reordered, iterate according to new order
    : [...this.valueSizesMap.keys()];
    valueSizesIterable.forEach(key => {
      const val = this.valueSizesMap.get(key);
      if (!draggablesCombinedWidthPerDropZone || draggablesCombinedWidthPerDropZone + val < MAX_DROPZONE_WIDTH) {
        draggablesArray.push(key);
        draggablesCombinedWidthPerDropZone += val;
      } else {
        valuesFormattedArray.push(draggablesArray);
        draggablesCombinedWidthPerDropZone = val;
        draggablesArray = [key];
      }
    });
    valuesFormattedArray.push(draggablesArray);
    this.setState({
      values: valuesFormattedArray
    });
  }
  triggerOpen() {
    this.props.onOpenChange(SyntheticEvent(true));
  }
  onDragEnd(result) {
    const {
      destination,
      source
    } = result;
    const {
      id: UIFormControlId
    } = this.props;

    // dropped outside the list
    if (!destination) {
      return;
    }
    let newValuesArray = [...this.state.values];
    const indexPositionInId = `droppable-${UIFormControlId}--`.length;
    const sourceListIndex = +source.droppableId.substring(indexPositionInId); // index of source list of dragged item in values array of arrays as number
    const destinationListIndex = +destination.droppableId.substring(indexPositionInId); // index of destination list of dragged item in values array of arrays as number
    const sourceListCopy = this.state.values[sourceListIndex].slice(0);
    if (sourceListIndex === destinationListIndex) {
      const [removedValue] = sourceListCopy.splice(source.index, 1);
      sourceListCopy.splice(destination.index, 0, removedValue); // update list to reflect moved item
      newValuesArray[sourceListIndex] = sourceListCopy; // update entire values array of arrays with updated list
      newValuesArray = newValuesArray.flat();
    } else {
      const destinationListCopy = this.state.values[destinationListIndex].slice(0);
      const [removed] = sourceListCopy.splice(source.index, 1);
      destinationListCopy.splice(destination.index, 0, removed);
      newValuesArray[sourceListIndex] = sourceListCopy;
      newValuesArray[destinationListIndex] = destinationListCopy;
      newValuesArray = newValuesArray.filter(dropZoneValuesArray => dropZoneValuesArray.length) // update entire values array of arrays, don't include any lists that are now empty after the item has moved
      .flat();
    }
    this.formatDropZoneListValues(newValuesArray); // pass new values arrangement to formatting function to ensure size limits are complied with for each row/list of values
  }
  renderDraggableValues(draggablesList, dndAreaIndex) {
    const {
      id: UIFormControlId,
      optionsCache,
      valueRenderer
    } = this.props;
    return draggablesList.map((val, index) => {
      const option = optionsCache[val] || {
        text: val,
        value: val
      };
      const valueNode = valueRenderer(option);
      return /*#__PURE__*/_jsx(Draggable, {
        draggableId: `draggable-${UIFormControlId}--${val}`,
        index: index,
        children: (provided /*, snapshot*/) => /*#__PURE__*/_jsx("div", Object.assign({
          ref: element => {
            if (element && !this.valueSizesMap.has(val)) {
              this.valueSizesMap.set(val, element.offsetWidth);
            }
            provided.innerRef(element);
          }
        }, provided.draggableProps, provided.dragHandleProps, {
          style: Object.assign({
            maxWidth: `${MAX_DROPZONE_WIDTH - 8}px`
          }, provided.draggableProps.style),
          children: /*#__PURE__*/_jsx(MultiSelectValue, {
            className: dndAreaIndex === this.state.values.length - 1 ? 'm-y-0' : 'm-bottom-0',
            closeable: false,
            style: {
              maxWidth: 'calc(100% - 8px)'
            } // 8 is the padding on UITag
            ,
            value: option,
            children: valueNode
          }, `${UIFormControlId}--${val}`)
        }))
      }, `draggable-${UIFormControlId}--${val}`);
    });
  }
  renderDragAndDrop() {
    const {
      id: UIFormControlId
    } = this.props;
    return /*#__PURE__*/_jsx(DragDropContext, {
      onDragEnd: this.onDragEnd,
      children: this.state.values.map((draggablesList, dndAreaIndex) => /*#__PURE__*/_jsx(Droppable, {
        droppableId: `droppable-${UIFormControlId}--${dndAreaIndex}`,
        direction: "horizontal",
        children: (provided /*, snapshot*/) => {
          return /*#__PURE__*/_jsx(StyledDropZone, Object.assign({
            isLastZone: dndAreaIndex === this.state.values.length - 1,
            ref: provided.innerRef
          }, provided.droppableProps, {
            children: /*#__PURE__*/_jsxs(_Fragment, {
              children: [this.renderDraggableValues(draggablesList, dndAreaIndex), provided.placeholder]
            })
          }));
        }
      }, `droppable-${UIFormControlId}--${dndAreaIndex}`))
    });
  }
  render() {
    const {
      className,
      error,
      id: UIFormControlId,
      readOnly,
      required,
      remainingPropsForAria
    } = this.props;
    // We use the same classnames as react-select here, to get the same appearance.
    /* eslint-disable hubspot-dev/no-private-classes */
    const combinedAnchorClassName = classNames(className, 'private-form__selectplus Select Select--multi private-form__selectplus--multi has-value', error && 'private-form__control--error', readOnly && 'is-readonly');
    /* eslint-enable hubspot-dev/no-private-classes */

    return /*#__PURE__*/_jsx("div", Object.assign({}, getAriaAndDataProps(remainingPropsForAria), {
      className: combinedAnchorClassName,
      "data-collapsed": true,
      children: /*#__PURE__*/_jsxs("div", {
        className: "Select-control",
        style: {
          height: this.state.values.length === 1 ? '40px' : 'auto'
        },
        children: [/*#__PURE__*/_jsxs("span", {
          className: "Select-multi-value-wrapper",
          id: `react-select-${UIFormControlId}--value`,
          style: {
            width: 'auto'
          },
          children: [this.renderDragAndDrop(), /*#__PURE__*/_jsx("div", {
            className: "Select-input",
            style: {
              display: 'inline-block'
            },
            children: /*#__PURE__*/_jsx(StyledInput, {
              readOnly: true,
              required: required,
              autoComplete: "off",
              role: "combobox",
              "aria-expanded": "false",
              "aria-haspopup": "menu",
              "aria-activedescendant": `react-select-${UIFormControlId}--value`,
              value: ""
            })
          })]
        }), /*#__PURE__*/_jsx(StyledUIClickable, {
          onClick: this.triggerOpen
        }), /*#__PURE__*/_jsx(StyledArrow, {
          className: "Select-arrow-zone",
          onClick: this.triggerOpen,
          children: /*#__PURE__*/_jsx("div", {
            className: "Select-arrow"
          })
        })]
      })
    }));
  }
}